// src/services/tableSplitService.js
import apiClient from './api/client';
import ENDPOINTS from './api/endpoints';

/**
 * Table Split Service
 * Handles all API operations related to table splits
 */
export const tableSplitService = {
  /**
   * Get all table splits
   * @returns {Promise} List of all table splits
   */
  getAllSplits: async () => {
    try {
      const response = await apiClient.get(ENDPOINTS.TABLE_SPLITS.GET_ALL);
      return response;
    } catch (error) {
      console.error('❌ Failed to fetch table splits:', error);
      throw error;
    }
  },

  /**
   * Get splits for a specific table
   * @param {number|string} tableId - Table ID
   * @returns {Promise} Splits for that table
   */
  getSplitsByTable: async (tableId) => {
    try {
      const response = await apiClient.get(ENDPOINTS.TABLE_SPLITS.GET_BY_TABLE(tableId));
      return response;
    } catch (error) {
      console.error(`❌ Failed to fetch splits for table ${tableId}:`, error);
      throw error;
    }
  },

  /**
   * Get a specific split by its ID
   * @param {number|string} splitId - Split ID
   * @returns {Promise} Single split object
   */
  getSplitById: async (splitId) => {
    try {
      const response = await apiClient.get(ENDPOINTS.TABLE_SPLITS.GET_BY_ID(splitId));
      return response;
    } catch (error) {
      console.error(`❌ Failed to fetch split with ID ${splitId}:`, error);
      throw error;
    }
  },

  /**
   * Create a new table split
   * @param {Object} splitData - Split details (tableId, pax, etc.)
   * @returns {Promise} Created split
   */
  createSplit: async (splitData) => {
    try {
      const response = await apiClient.post(ENDPOINTS.TABLE_SPLITS.CREATE, splitData);
      return response;
    } catch (error) {
      console.error('❌ Failed to create table split:', error);
      throw error;
    }
  },

  /**
   * Update split status
   * @param {number|string} splitId - Split ID
   * @param {string} status - New status
   * @returns {Promise} Updated split
   */
  updateSplitStatus: async (splitId, status) => {
    try {
      // Backend expects status as query parameter, not body
      const response = await apiClient.put(
        `${ENDPOINTS.TABLE_SPLITS.UPDATE_STATUS(splitId)}?status=${status}`
      );
      return response;
    } catch (error) {
      console.error(`❌ Failed to update status for split ${splitId}:`, error);
      throw error;
    }
  },

  /**
 * Update entire split (used for people count update)
 * @param {number} tableId 
 * @param {string} splitName 
 * @param {object} data 
 */
updateSplit: async (tableId, splitName, data) => {
  try {
    const response = await apiClient.put(
      ENDPOINTS.TABLE_SPLITS.UPDATE_SPLIT(tableId, splitName),
      data    // body: { peopleCount: X }
    );
    return response;
  } catch (error) {
    console.error("❌ Failed to update split:", error);
    throw error;
  }
},



  /**
   * Update number of people in split
   * @param {number|string} splitId - Split ID
   * @param {number} peopleCount - Updated people count
   * @returns {Promise} Updated split data
   */
  updatePeopleCount: async (splitId, peopleCount) => {
    try {
      const response = await apiClient.put(
        ENDPOINTS.TABLE_SPLITS.UPDATE_PEOPLE_COUNT(splitId),
        { peopleCount }
      );
      return response;
    } catch (error) {
      console.error(`❌ Failed to update people count for split ${splitId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a specific table split
   * @param {number|string} splitId - Split ID
   * @returns {Promise} Deletion confirmation
   */
  deleteSplit: async (splitId) => {
    try {
      const response = await apiClient.delete(ENDPOINTS.TABLE_SPLITS.DELETE(splitId));
      return response;
    } catch (error) {
      console.error(`❌ Failed to delete split ${splitId}:`, error);
      throw error;
    }
  },

  /**
   * Cleanup counter splits (delete stale/abandoned splits)
   * @param {number|string} tableId - Counter table ID
   * @returns {Promise} Cleanup result with deletedCount
   */
  cleanupCounterSplits: async (tableId) => {
    try {
      const response = await apiClient.delete(ENDPOINTS.TABLE_SPLITS.CLEANUP_COUNTER(tableId));
      return response;
    } catch (error) {
      console.error(`❌ Failed to cleanup counter splits for table ${tableId}:`, error);
      throw error;
    }
  },

  /**
   * Clear all splits for a specific table
   * @param {number|string} tableId - Table ID
   * @returns {Promise} Operation result
   */
  clearTableSplits: async (tableId) => {
    try {
      const response = await apiClient.delete(ENDPOINTS.TABLE_SPLITS.CLEAR_TABLE(tableId));
      return response;
    } catch (error) {
      console.error(`❌ Failed to clear splits for table ${tableId}:`, error);
      throw error;
    }
  },
};

export default tableSplitService;
